import { customComponents } from "hec/builder";
import { GuardedBuilderLink } from "hec/builder/guarded-builder-link";
import type React from "react";

import { type BuilderContent, Content } from "@builder.io/sdk-react";

import { PrefetchBuilderAnchors } from "~/hec/components/prefetch-builder-anchors";
import { useBuilder } from "~/hec/hooks/useBuilder";
import { cn } from "~/lib/ui";

import type {
  MarketingTileItem,
  MarketingTileSpan,
} from "./interleave-marketing-tiles";

const SPAN_FALLBACK: MarketingTileSpan = "1";

// Extracts a CSS aspect-ratio value ("W / H") from an SVG viewBox attribute
// embedded in raw HTML. External Fastr tiles use position:absolute children
// that collapse the wrapper to 0 height when the tile is alone in a CSS Grid
// row (mobile 1-column layout). Setting aspect-ratio on the wrapper gives it
// intrinsic height so the absolutely-positioned content fills it correctly.
function extractSvgViewBoxRatio(html: string): string | undefined {
  const match = html.match(
    /viewBox=["']0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)['"]/,
  );
  if (!match) return undefined;
  return `${match[1]} / ${match[2]}`;
}

// Per-breakpoint Tailwind classes for each span value. Each map's value is a
// space-separated string with the visibility toggle (`hidden` / `block`) and
// the `col-span-N` for that breakpoint. Class strings are literal so
// Tailwind's content scanner picks them up.
//
// Span values are author-controlled per breakpoint; if an out-of-range value
// is set (e.g. mobileSpan=3 in a 2-col grid), CSS Grid silently clamps the
// span to the row's column count.
const MOBILE_SPAN: Record<MarketingTileSpan, string> = {
  "0": "hidden",
  "1": "block col-span-1",
  "2": "block col-span-2",
  "3": "block col-span-3",
  "4": "block col-span-4",
};

const TABLET_SPAN: Record<MarketingTileSpan, string> = {
  "0": "sm:hidden",
  "1": "sm:block sm:col-span-1",
  "2": "sm:block sm:col-span-2",
  "3": "sm:block sm:col-span-3",
  "4": "sm:block sm:col-span-4",
};

const DESKTOP_SPAN: Record<MarketingTileSpan, string> = {
  "0": "md:hidden",
  "1": "md:block md:col-span-1",
  "2": "md:block md:col-span-2",
  "3": "md:block md:col-span-3",
  "4": "md:block md:col-span-4",
};

const LARGE_DESKTOP_SPAN: Record<MarketingTileSpan, string> = {
  "0": "xl:hidden",
  "1": "xl:block xl:col-span-1",
  "2": "xl:block xl:col-span-2",
  "3": "xl:block xl:col-span-3",
  "4": "xl:block xl:col-span-4",
};

export function MarketingTileSlot({
  tile,
  className,
}: {
  tile: MarketingTileItem;
  className?: string;
}) {
  const builder = useBuilder();
  const inner = tile.content?.value;
  const innerData = inner?.data;

  // The wrapping `injectable-marketing-content` model is a data model — it
  // doesn't carry its own renderable blocks. The two valid render paths are
  // raw embedded HTML (when `isExternalContentSource` is true) or the nested
  // `builderContent` reference (typically a Symbol). The nested reference (and
  // the link references nested inside it) are resolved by the loader's
  // `enrich: true` fetch of `category-marketing-tiles`.
  const isExternalContentSource = innerData?.isExternalContentSource === true;
  const embeddedContent = innerData?.embeddedContent;
  const nestedRef = innerData?.builderContent;
  const nestedContent = nestedRef?.value;
  const nestedModel = nestedRef?.model;

  const renderable = isExternalContentSource
    ? typeof embeddedContent === "string" && embeddedContent.length > 0
    : Boolean(nestedContent && nestedModel);
  if (!renderable) return null;

  const mobileSpan = tile.mobileSpan ?? SPAN_FALLBACK;
  const tabletSpan = tile.tabletSpan ?? SPAN_FALLBACK;
  const desktopSpan = tile.desktopSpan ?? SPAN_FALLBACK;
  const largeDesktopSpan = tile.largeDesktopSpan ?? SPAN_FALLBACK;

  // External tiles (Fastr embeds) use `position:absolute; height:100%` on
  // their root element. On mobile (1-column grid) the tile is alone in its
  // row, so the row has no product-tile neighbours to provide row height —
  // the wrapper collapses to 0 and the absolutely-positioned content is
  // invisible. Giving the wrapper an explicit aspect-ratio fixes this: the
  // SVG viewBox is extracted from the embedded HTML as a fallback when the
  // author hasn't supplied one via `tile.aspectRatio`.
  let wrapperStyle: React.CSSProperties | undefined;
  if (tile.aspectRatio) {
    wrapperStyle = { aspectRatio: tile.aspectRatio };
  } else if (isExternalContentSource && typeof embeddedContent === "string") {
    const ratio = extractSvgViewBoxRatio(embeddedContent);
    if (ratio) wrapperStyle = { aspectRatio: ratio };
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        MOBILE_SPAN[mobileSpan],
        TABLET_SPAN[tabletSpan],
        DESKTOP_SPAN[desktopSpan],
        LARGE_DESKTOP_SPAN[largeDesktopSpan],
        className,
      )}
      style={wrapperStyle}
      data-testid="plp-marketing-tile"
    >
      <PrefetchBuilderAnchors>
        {isExternalContentSource ? (
          <div
            dangerouslySetInnerHTML={{ __html: embeddedContent as string }}
          />
        ) : (
          <Content
            model={nestedModel as string}
            content={nestedContent as BuilderContent}
            apiKey={builder.config.api_key}
            customComponents={customComponents}
            linkComponent={GuardedBuilderLink}
          />
        )}
      </PrefetchBuilderAnchors>
    </div>
  );
}
